import * as React from 'react';
import {useEffect} from 'react';
import careStore from "../../store/careStore";
import {relative} from 'path';
import SelfCheck from './SelfCheck';
import Top from "../Top";
import Bottom from "../Bottom";
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import {useTheme} from '@mui/material/styles';
import MobileStepper from '@mui/material/MobileStepper';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import Stack from '@mui/material/Stack';
import {useLocation} from "react-router-dom";
import { selfQuestionnaires } from "../../utils/selfQuestionnaires";
import checkStore from "../../store/checkStore";
import StateMissingFallback from "../../components/StateMissingFallback";

const CheckStep = () => {
  const storeData: any = checkStore();
  const theme = useTheme();
  const [activeStep, setActiveStep] = React.useState(0);
  const [skipped, setSkipped] = React.useState(new Set<number>());
  const [completed, setCompleted] = React.useState<{
    [k: number]: boolean;
  }>({});
  const {state} = useLocation();
  const {selfQuestionNo} = state ?? {};
  const [questionAskCount, setQuestionAskCount] = React.useState(0);
  /**
   * 이 설문지. state 가 없거나(새로고침·직접 진입) 모르는 번호면 undefined 다.
   * 예전에는 여기서 곧바로 `[0].questions` 를 읽어 TypeError 가 났고,
   * 아래 `if (!state) return null` 가드는 그보다 뒤라 아무 소용이 없었다
   * (렌더 함수 본문이 위에서부터 실행되므로 가드에 닿기 전에 죽는다).
   */
  const questionnaire: any = selfQuestionnaires.find((item: any) => item.no === selfQuestionNo);
  const steps = new Array(Math.max(0, (questionnaire?.questions?.length ?? 1) - 1));

  useEffect(() => {
    // early return 과 달리 effect 는 실행되므로 여기서도 막는다
    if (!state) return;
    setQuestionAskCount(questionnaire.questions.length - 1);

    return () => {
      storeData.setSelectedAnswerList([]);
    }
  }, []);

  const isStepSkipped = (step: number) => {
    return skipped.has(step);
  };
 
  const handleNext = () => {
    if(storeData.selectedAnswerList.filter((v: any) => v.askNo === activeStep).length <= 0){ 
      alert('답변을 선택해주세요.');
    }else{
      let newSkipped = skipped;
  
      if (isStepSkipped(activeStep)) {
        newSkipped = new Set(newSkipped.values());
        newSkipped.delete(activeStep);
      }
  
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
      setSkipped(newSkipped);
    }
  };

  const handleBack = () => {
    // 백버튼 누르기전에 항목을 선택했었다면 선택한 값 삭제 처리
    let tempList = storeData.selectedAnswerList;
    storeData.setSelectedAnswerList(tempList.filter((v: any) => v.askNo < activeStep - 1));
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const totalSteps = () => {
    return steps.length;
  };

  const completedSteps = () => {
    return Object.keys(completed).length;
  };

  const allStepsCompleted = () => {
    return completedSteps() === totalSteps();
  };

  const drawStepContent = (activeStep: number) => {
    return (
      <SelfCheck
        key={activeStep}
        selfQuestionNo={questionnaire.no}
        resultType={questionnaire.resultType}
        resultTitle={questionnaire.resultTitle}
        item={questionnaire.questions[activeStep]}
        questionTotalCount={questionnaire.questions.length}
      />
    )
  };
  
  

  // state 없이 진입하면(새로고침·직접 URL 접근) 렌더할 데이터가 없다.
  // 아래에서 undefined 의 속성을 읽어 화면이 깨지므로 렌더하지 않는다.
  // state 가 없거나(새로고침·딥링크) 모르는 설문 번호면 안내를 보여준다
  if (!state || !questionnaire) {
    return (
      <StateMissingFallback
        title="자가진단"
        message={'자가진단 정보를 불러오지 못했습니다.\n목록에서 다시 선택해 주세요.'}
        actionLabel="자가진단 목록으로"
        to="/check/SelfCheckList"
      />
    );
  }

  return (
    <Stack sx={{ width: '100%', minWidth: 320, background: '#F1F2F4' }} spacing={2}>
      <Top title={questionnaire.title} />

      <Box sx={{ position: 'relative', mt: '60px !important' }}>

        <Box sx={{ bgcolor: '#FFFBF8', p: 1, pl: 2, pr: 2 }}>
          <Typography component="div" sx={{ fontSize: 13, pb: 0, color: '#333', mb: 0, }}>
            {questionnaire.desc}
          </Typography>
        </Box>

        <Box sx={{ p: 2,pt:3, pb:1 }}>

          <MobileStepper
            variant="progress"
            steps={steps.length + 1}
            position="static"
            activeStep={activeStep}
            sx={{
              width: '100%', flexGrow: 1, p: 0, bgcolor: '#F1F2F4', boxSizing: 'border-box',
              '& .MuiLinearProgress-root': { width: '100%', bgcolor: '#fff', },
              '& .MuiLinearProgress-barColorPrimary': { bgcolor: '#EF8322' }
            }}
            nextButton={
              <Button size="small" onClick={handleNext} disabled={activeStep === questionAskCount} sx={{ display: 'none' }}>
                Next
              </Button>
            }
            backButton={
              <Button size="small" onClick={handleBack} disabled={activeStep === 0} sx={{ display: 'none' }}>
                Back
              </Button>
            }
          />

          <Typography sx={{ textAlign: 'right', fontSize: 12, color: '#777', mt: .5 }}><span style={{ fontWeight: 700, color: '#555' }}>{activeStep + 1}</span> / {steps.length + 1} </Typography>
        </Box>

        <Box>
          {(drawStepContent(activeStep))}
        </Box>

        <MobileStepper
          variant="progress"
          steps={steps.length + 1}
          position="static"
          activeStep={activeStep}
          sx={{
            width: '100%', flexGrow: 1, p: '0 20px 0px 20px', mb: 10, mt: 3, bgcolor: '#eeeff1', boxSizing: 'border-box',
            '& .MuiLinearProgress-root': { display: 'none' }
          }}
          nextButton={
            <Button size="small" onClick={handleNext} disabled={activeStep === questionAskCount} sx={stepNextBtn}>
              Next
              {theme.direction === 'rtl' ? (
                <KeyboardArrowLeft />
              ) : (
                <KeyboardArrowRight />
              )}
            </Button>
          }
          backButton={
            <Button size="small" onClick={handleBack} disabled={activeStep === 0} sx={stepBackBtn}>
              {theme.direction === 'rtl' ? (
                <KeyboardArrowRight />
              ) : (
                <KeyboardArrowLeft />
              )}
              Back
            </Button>
          }
        />
      </Box>
      <Bottom />
    </Stack >
  );
}

const stepBackBtn = {
  bgcolor: '#fff',
    color: '#ef8322',
  
  borderRadius: '30px 0 0 30px',
  border: '1px solid #ef8322',
  width: '48%',
  p: '10px',
  '&.MuiButtonBase-root:hover': {
     bgcolor: '#fff',
  color: '#ef8322',
  },
  '&.Mui-disabled': {
    border: '1px solid #ccc',
    bgcolor: '#f5f5f5',
  }
}

const stepNextBtn = {
  bgcolor: '#fff ',
  color: '#ef8322',
  borderRadius: '0 30px 30px 0 ',
  border: '1px solid #ef8322',
  width: '48%',
  p: '10px',
  '&.MuiButtonBase-root:hover': {
  
    bgcolor: '#fff ',
    color: '#ef8322',
  },
  '&.Mui-disabled': {
    border: '1px solid #ccc',
    bgcolor: '#f5f5f5',
  }
}

export default CheckStep;